import { Glue42 } from "../../glue";

const CONTEXT_PREFIX = "___channel___";
const LATEST_FDC3_TYPE = "latest_fdc3_type";

export class SharedContextSubscriber {
    private unsubscribeFunc: () => void;
    private callback: any;

    constructor(private contexts: Glue42.Contexts.API) {
    }

    public subscribe(callback: any): void {
        this.callback = callback;
    }

    public subscribeFor(name: string, callback: (data: object, context: Glue42.Channels.ChannelContext, updaterId: string) => void): Promise<() => void> {
        if (!this.isChannel(name)) {
            return Promise.reject(new Error(`Channel with name: ${name} doesn't exist!`));
        }

        const contextName = this.createContextName(name);

        return this.contexts.subscribe(contextName, (data, _, __, ___, extraData) => {
            callback(data.data, data, extraData?.updaterId);
        });
    }

    /** Unsubscribes from the current shared context, and subscribe to the passed one */
    public async switchChannel(name: string): Promise<void> {
        this.unsubscribe();
        const contextName = this.createContextName(name);
        this.unsubscribeFunc = await this.contexts.subscribe(contextName, (data, _, __, ___, extraData) => {
            if (this.callback) {
                this.callback(data.data, data, extraData?.updaterId);
            }
        });
    }

    public leave() {
        if (this.callback) {
            this.callback({}, undefined);
        }
        this.unsubscribe();
    }

    public all(): string[] {
        const contextNames = this.contexts.all();
        const channelContextNames = contextNames.filter((contextName) => contextName.startsWith(CONTEXT_PREFIX));
        const channelNames = channelContextNames.map((channelContextName) => channelContextName.substr(CONTEXT_PREFIX.length));
        return channelNames;
    }

    public async getContextData(name: string): Promise<Glue42.Channels.ChannelContext> {
        if (!this.isChannel(name)) {
            throw new Error(`A channel with name: ${name} doesn't exist!`);
        }

        const contextName = this.createContextName(name);

        const contextData = await this.contexts.get(contextName);

        if (contextData[LATEST_FDC3_TYPE]) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { latest_fdc3_type, ...data } = contextData;

            return data;
        }

        return contextData;
    }

    public updateChannel(name: string, data: { name: string, meta: any, data: any }): Promise<void> {
        const contextName = this.createContextName(name);
        return this.contexts.update(contextName, data);
    }

    public updateData(name: string, data: any) {
        const contextName = this.createContextName(name);
        const fdc3Type = this.getFDC3Type(data);

        if (this.contexts.setPathSupported) {
            const pathValues: Glue42.Contexts.PathValue[] = Object.keys(data).map((key) => {
                return {
                    path: `data.` + key,
                    value: data[key]
                };
            });

            if (fdc3Type) {
                pathValues.push({ path: LATEST_FDC3_TYPE, value: fdc3Type });
            }

            return this.contexts.setPaths(contextName, pathValues);
        } else {
            if (fdc3Type) {
                data[LATEST_FDC3_TYPE] = fdc3Type;
            }

            // pre 3.10, note that we update the data property only
            return this.contexts.update(contextName, { data });
        }
    }

    public isChannel(name: string): boolean {
        return this.all().some((channelName) => channelName === name);
    }

    public async remove(name: string) {
        if (!this.isChannel(name)) {
            throw new Error(`A channel with name: ${name} doesn't exist!`);
        }

        const contextName = this.createContextName(name);
        await this.contexts.destroy(contextName);
    }

    private unsubscribe(): void {
        if (this.unsubscribeFunc) {
            this.unsubscribeFunc();
        }
    }

    private createContextName(name: string): string {
        return CONTEXT_PREFIX + name;
    }

    private getFDC3Type(data: any): string | undefined {
        const fdc3PropsArr = Object.keys(data).filter((key: string) => key.indexOf("fdc3_") === 0);

        if (fdc3PropsArr.length === 0) {
            return;
        }

        if (fdc3PropsArr.length > 1) {
            throw new Error("FDC3 does not support updating of multiple context keys");
        }

        return fdc3PropsArr[0].split("_").slice(1).join("_");
    }
}
